import { Injectable, inject } from '@angular/core';

import { HapticsConfiguration } from '@frontend/sports/common/client-config-data-access';
import { DeviceService, NativeAppService, WINDOW } from '@frontend/vanilla/core';
import { catchError, first, map, of } from 'rxjs';

import {
    HapticAction,
    HapticPattern,
    HapticsConfigurationOnly,
    defaultHapticsConfig,
    defaultiOSPattern,
    iOSHapticFeedbackModel,
    iOSPattern,
    webPattern,
} from './haptic.models';

@Injectable({ providedIn: 'root' })
export class HapticsService {
    private readonly _window = inject(WINDOW);
    private readonly deviceService = inject(DeviceService);
    private readonly nativeApp = inject(NativeAppService);

    private haptics = defaultHapticsConfig;
    private hasHapticsSupport = 'vibrate' in this._window.navigator;

    constructor() {
        const hapticConfig = inject(HapticsConfiguration);
        hapticConfig.whenReady
            .pipe(
                first(),
                map(() => hapticConfig),
                catchError(() => of(defaultHapticsConfig)),
            )
            .subscribe((data) => {
                this.haptics = {
                    isEnabled: data.isEnabled,
                    actionWithPattern: data.actionWithPattern,
                } satisfies HapticsConfigurationOnly;
            });
    }

    vibrate(action: HapticAction): void {
        if (!this.haptics.isEnabled) {
            return;
        }

        const pattern: HapticPattern = (this.haptics.actionWithPattern[action] as HapticPattern) ?? 'short';

        if (this.nativeApp.isNative) {
            if (this.deviceService.isiOS) {
                this.vibrateNative(iOSPattern[pattern] ?? defaultiOSPattern);
            } else if (this.deviceService.isAndroid && this.hasHapticsSupport) {
                // for now Android native doesn't support native vibration
                this.vibrateBrowser(webPattern[pattern] ?? [1]);
            }
        } else if (this.hasHapticsSupport) {
            this.vibrateBrowser(webPattern[pattern] ?? [1]);
        }
    }

    private vibrateBrowser = (pattern: number[]): void => {
        this._window.navigator.vibrate(pattern);
    };

    private vibrateNative = (pattern: iOSHapticFeedbackModel): void => {
        this.nativeApp.sendToNative({
            eventName: 'HapticFeedback',
            parameters: pattern,
        });
    };
}
